import { useState } from "react";
import { z } from "zod";
import { useUsers } from "../../api/queries";
import { Combobox, type ComboboxOption } from "../../components/Combobox";

/** 共有の相手に使えるメール(@zenk.co.jp)。 */
const shareEmailSchema = z.email().endsWith("@zenk.co.jp");

/**
 * 共有の相手を選ぶ(ユーザー要望 2026-10-15)。ユーザーマスタから、名前・よみがな・メールで絞り込む。
 * exclude(所有者・共有済み・選んだ人など)とアーカイブした人は候補に出さない。
 * 一覧にいない人も、@zenk.co.jp のメールを丸ごと入れれば選べる(まだログインしていない人など)。
 */
export function ShareUserPicker({
  id,
  value,
  onChange,
  exclude,
  enabled = true,
  className,
}: {
  id: string;
  value: string;
  onChange: (email: string) => void;
  exclude: ReadonlySet<string>;
  enabled?: boolean;
  className?: string;
}) {
  // 開くたびに取り直す(ホームで読んだあとにログインした人も選べるように)
  const users = useUsers(enabled, { fresh: true });
  const [search, setSearch] = useState("");
  const options: ComboboxOption[] = (users.data?.users ?? [])
    .filter((u) => !u.archived && !exclude.has(u.email))
    .map((u) => ({
      value: u.email,
      // 名前が無ければメールで出す
      label: u.nameRegistered ? u.displayName : u.email,
      ...(u.nameRegistered ? { description: u.email } : {}),
      keywords: [`${u.familyNameKana} ${u.givenNameKana}`, u.email],
    }));
  const typed = search.trim().toLowerCase();
  const typedEmail =
    shareEmailSchema.safeParse(typed).success &&
    !exclude.has(typed) &&
    !options.some((o) => o.value === typed)
      ? typed
      : null;
  if (value && !options.some((o) => o.value === value)) options.push({ value, label: value });

  return (
    <Combobox
      id={id}
      {...(className ? { className } : {})}
      value={value}
      onChange={onChange}
      options={options}
      placeholder="名前・メールで検索"
      searchPlaceholder="名前・よみがな・メール"
      emptyText="見つかりません(@zenk.co.jp のメールを丸ごと入れると追加できます)"
      onSearchChange={setSearch}
      create={
        typedEmail
          ? { label: `「${typedEmail}」を追加`, onSelect: () => onChange(typedEmail) }
          : null
      }
    />
  );
}

/** メールから名前を引く(名前が未登録なら null)。共有している人の一覧に出す。 */
export function useUserName(enabled = true): (email: string) => string | null {
  const users = useUsers(enabled);
  const all = users.data?.users ?? [];
  return (email) => {
    const u = all.find((x) => x.email === email);
    return u?.nameRegistered ? u.displayName : null;
  };
}

/** 名前(あれば)と、小さくメール。名前が無ければメールだけ。 */
export function PersonName({ email, name }: { email: string; name: string | null }) {
  return name ? (
    <span className="flex min-w-0 flex-col">
      <span className="truncate">{name}</span>
      <span className="truncate text-xs text-muted-foreground">{email}</span>
    </span>
  ) : (
    <span className="truncate">{email}</span>
  );
}
